import { useCallback } from 'react';

import { useAtomCallbackMemoized } from '../hooks/useAtomCallbackMemoized';
import { isWhiteboardModeAtom } from '../session/messages/state';
import { useSendWhiteboardSnapshotCallback } from '../session/messages/useSendWhiteboardSnapshotCallback';
import type { RemoteParticipantWrapperProps } from '../session/session-grid/RemoteParticipantCell';
import { RemoteParticipantWrapper as BaseRemoteParticipant } from '../session/session-grid/RemoteParticipantCellWrapper';
import { UserType } from '../types';
import {
  type RemoteDataDelegator,
  type RemoteMediaDelegator,
  type RemoteParticipantMediaEventHandler,
  remoteDataDelegatorsAtom,
} from '../video-sdk';
import { useHandleReceivedMessageCallback } from './messages/useHandleReceivedMessageCallback';
import { useSendProviderParticipantStateCallback } from './messages/useSendProviderParticipantStateCallback';
import { providerShortIdAtom } from './state';
import { useTrackSessionParticipantViewedEvent } from './useTrackSessionParticipantViewedEvent';

function checkShouldSendWhiteboardSnapshot(
  storedRemoteDataDelegators: RemoteDataDelegator[],
  subscribedDataDelegator: RemoteMediaDelegator,
  isWhiteboardMode: boolean,
) {
  const isAlreadySubscribedToDataTrack = storedRemoteDataDelegators.some(
    (storedTrack) => storedTrack.sid === subscribedDataDelegator.sid,
  );
  return isWhiteboardMode && !isAlreadySubscribedToDataTrack;
}

export function RemoteParticipant(props: Omit<RemoteParticipantWrapperProps, 'onMessageReceived'>) {
  const handleReceivedMessage = useHandleReceivedMessageCallback();
  useTrackSessionParticipantViewedEvent(props.remoteParticipant.identity);
  const getIsWhiteboardMode = useAtomCallbackMemoized((get) => get(isWhiteboardModeAtom), []);
  const sendWhiteboardSnapshot = useSendWhiteboardSnapshotCallback();
  const getProviderShortId = useAtomCallbackMemoized((get) => get(providerShortIdAtom), []);
  const getRemoteDataDelegators = useAtomCallbackMemoized((get) => get(remoteDataDelegatorsAtom), []);

  const sendParticipantState = useSendProviderParticipantStateCallback();

  const onDataTrackSubscribed: RemoteParticipantMediaEventHandler = useCallback(
    async (subscribedMedia) => {
      sendParticipantState();
      const shouldSendWhiteboardSnapshot = checkShouldSendWhiteboardSnapshot(
        getRemoteDataDelegators(),
        subscribedMedia,
        getIsWhiteboardMode().value,
      );
      if (shouldSendWhiteboardSnapshot) {
        await sendWhiteboardSnapshot({
          entityType: UserType.PROVIDER,
          entityId: getProviderShortId() ?? '',
        });
      }
    },
    [getIsWhiteboardMode, getProviderShortId, getRemoteDataDelegators, sendParticipantState, sendWhiteboardSnapshot],
  );

  return (
    <BaseRemoteParticipant
      {...props}
      onMessageReceived={handleReceivedMessage}
      onDataTrackSubscribed={onDataTrackSubscribed}
    />
  );
}
